import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isStaff, ROLE_LABELS, normRole } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/schemes", label: "Schemes" },
  { to: "/privacy-policy", label: "Privacy" },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const staff = isStaff(user?.role);
  const dashboardPath = staff ? "/admin" : "/dashboard";

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-lg border-b border-emerald-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo-link">
            <img src="/logo.png" alt="NovaAgri" className="h-9 w-9 rounded-lg object-contain" />
            <div className="leading-tight">
              <div className="font-head font-extrabold text-[15px] sm:text-base text-emerald-950">NovaAgri Solution LLP</div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-emerald-700/70 font-semibold">Agriculture Technology</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}-link`}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "text-emerald-800 bg-emerald-50" : "text-slate-600 hover:text-emerald-800 hover:bg-emerald-50/60"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+918762765882" className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800" data-testid="header-phone">
              <Phone className="h-4 w-4" /> +91 87627 65882
            </a>
            {user ? (
              <div className="flex items-center gap-2">
                <Button variant="outline" className="rounded-full border-emerald-200 text-emerald-800" onClick={() => navigate(dashboardPath)} data-testid="header-dashboard-btn">
                  {staff ? <ShieldCheck className="h-4 w-4 mr-1.5" /> : <LayoutDashboard className="h-4 w-4 mr-1.5" />}
                  {staff ? (ROLE_LABELS[normRole(user.role)] || "Console") : "Dashboard"}
                </Button>
                <Button variant="ghost" size="icon" className="rounded-full text-slate-500" onClick={async () => { await logout(); navigate("/"); }} data-testid="header-logout-btn">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button className="rounded-full bg-brand hover:bg-emerald-800 text-white px-5 shadow-md shadow-emerald-900/10" onClick={() => navigate("/login")} data-testid="header-login-btn">
                Login / Apply
              </Button>
            )}
          </div>

          <button className="lg:hidden p-2 text-emerald-900" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-emerald-100 bg-white px-4 py-4 space-y-1">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-slate-700 font-medium hover:bg-emerald-50">
              {n.label}
            </NavLink>
          ))}
          <div className="pt-2">
            {user ? (
              <div className="flex gap-2">
                <Button className="flex-1 rounded-full bg-brand text-white" onClick={() => { setOpen(false); navigate(dashboardPath); }}>Dashboard</Button>
                <Button variant="outline" className="rounded-full" onClick={async () => { await logout(); setOpen(false); navigate("/"); }}>Logout</Button>
              </div>
            ) : (
              <Button className="w-full rounded-full bg-brand text-white" onClick={() => { setOpen(false); navigate("/login"); }} data-testid="mobile-login-btn">Login / Apply</Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
